/**
 * Content-Security-Policy bootstrap — runs once on app start.
 *
 * Why this lives in the React bundle (not in a server header or a `<meta>`
 * in index.html):
 *   - Single owner: nonce generation, the MutationObserver that stamps the
 *     nonce on dynamically injected `<style>` tags, and the policy text all
 *     live next to each other and can't drift.
 *   - The Go static server (cmd/serve/main.go) and IIS web.config deliberately
 *     do NOT set Content-Security-Policy, so the policy applied here is the
 *     only one in effect.
 *
 * Flow:
 *   1. Generate a fresh per-load nonce, expose it on window.__CSP_NONCE__ and
 *      as a data-attribute on <html> for any runtime consumer.
 *   2. Install a MutationObserver that stamps the nonce on every <style>
 *      element added to the document. Libraries like react-toastify and
 *      react-datepicker inject <style> tags at runtime — without the nonce
 *      they would be blocked by `style-src 'self' 'nonce-...'`.
 *   3. After a short delay, append a strict CSP <meta> to <head>. The delay
 *      gives Vite's module loader and any initial inline boot logic time to
 *      finish — browsers do NOT enforce CSP retroactively against scripts
 *      that have already executed.
 *
 * Dev mode is skipped: Vite's HMR client injects inline scripts and modules
 * that strict CSP would block. `import.meta.env.PROD` is statically `false`
 * in `vite dev`, so the bootstrap no-ops there.
 */

declare global {
  interface Window {
    __CSP_NONCE__?: string;
  }
}

const CSP_INJECTION_DELAY_MS = 5000;

function generateNonce(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function stampNonce(el: Element, nonce: string): void {
  if (el.nodeName === 'STYLE' && !el.hasAttribute('nonce')) {
    el.setAttribute('nonce', nonce);
  }
}

function stampAll(nonce: string): void {
  document.querySelectorAll('style:not([nonce])').forEach((el) => stampNonce(el, nonce));
}

function installObserver(nonce: string): void {
  const observer = new MutationObserver((mutations) => {
    let rescan = false;
    for (const m of mutations) {
      for (const node of Array.from(m.addedNodes)) {
        if (!(node instanceof Element)) continue;
        if (node.nodeName === 'STYLE') {
          stampNonce(node, nonce);
          rescan = true;
        } else {
          const inner = node.querySelectorAll?.('style:not([nonce])');
          if (inner && inner.length) {
            inner.forEach((el) => stampNonce(el, nonce));
            rescan = true;
          }
        }
      }
    }
    // A second pass after the microtask queue catches any cascade from the
    // library that just appended a node (e.g. a Shadow DOM that itself
    // injects a <style>).
    if (rescan) setTimeout(() => stampAll(nonce), 0);
  });
  observer.observe(document, { childList: true, subtree: true });
}

function policy(nonce: string): string {
  return [
    "default-src 'self'",
    "script-src 'self' https://maps.googleapis.com https://maps.gstatic.com https://*.googleapis.com https://*.gstatic.com",
    `style-src 'self' 'nonce-${nonce}' https://fonts.googleapis.com`,
    // img-src is intentionally broad (any HTTPS source) because image URLs are
    // built from `imagePrefix` returned by `/API/Partial/GetImageViewerSrcPrefix`
    // — the host varies per environment (dev2, staging, prod each return a
    // different prefix). Hard-coding `*.sgpdev.com` blocks any tenant whose
    // image server lives elsewhere. Widening to `https:` is safe: images cannot
    // execute scripts, and we still block data/blob exfiltration via http or
    // javascript: URIs. `data:` and `blob:` are explicit for inline previews.
    "img-src 'self' data: blob: https:",
    "font-src 'self' data: https://fonts.gstatic.com",
    // Google Maps JS API issues runtime fetch/XHR calls (e.g.
    // `maps.googleapis.com/$rpc/.../GetViewportInfo`) — these are governed by
    // connect-src, not script-src. Without the Google hosts here the Geomap /
    // VendorView routes' map tiles + viewport RPC are blocked on the deployed
    // (CSP-enforced) build, though they work in dev where CSP is a no-op.
    // Sentry event endpoint — see lib/errorReporting.ts. The DSN host is
    // regional (`oNNN.ingest.us.sentry.io`), which `*.ingest.sentry.io` does
    // NOT match (the wildcard only covers labels to the left of the suffix),
    // so the regional suffix is listed explicitly. `*.ingest.sentry.io`
    // covers non-regional DSNs.
    "connect-src 'self' https://*.sgpdev.com https://*.safeguardproperties.com https://authentication.sgpdev.com https://authentication.safeguardproperties.com wss://*.sgpdev.com wss://*.safeguardproperties.com https://maps.googleapis.com https://*.googleapis.com https://*.gstatic.com https://*.ingest.us.sentry.io https://*.ingest.sentry.io",
    "worker-src 'self' blob:",
    // `*.mindstudio.ai` covers the AI Companion iframe embedded on the
    // Overallowables page (P260 AI). The signed URL from
    // /API/MindStudio/ms/signedurl/get points at `https://app.mindstudio.ai/…`
    // in every environment (dev / staging / prod), so this whitelist is safe
    // to apply globally. Without it, prod blocked the iframe with a
    // `frame-src` CSP violation and the AI Companion never rendered.
    "frame-src 'self' https://*.sgpdev.com https://*.safeguardproperties.com https://*.mindstudio.ai",
    "object-src 'none'",
    "base-uri 'self'",
    // *.sgpdev.com covers cross-origin form POSTs to API hosts (e.g. VendorMemos
    // download submits a <form> to vmapi.sgpdev.com). Without this, the browser
    // blocks the submission with a form-action CSP violation.
    "form-action 'self' https://*.sgpdev.com https://authentication.sgpdev.com https://*.safeguardproperties.com https://authentication.safeguardproperties.com",
  ].join('; ');
}

function injectMeta(nonce: string): void {
  const meta = document.createElement('meta');
  meta.httpEquiv = 'Content-Security-Policy';
  meta.content = policy(nonce);
  document.head.appendChild(meta);
}

/**
 * Initialize CSP. Idempotent: safe to call multiple times — only the first
 * call wins. Vite's hot-reload occasionally re-runs the entry script, and
 * we don't want duplicate nonces or stacked observers.
 */
export function initCsp(): void {
  // Skip in Vite dev — HMR injects inline modules that strict CSP would block.
  if (!import.meta.env.PROD) return;

  if (window.__CSP_NONCE__) return;

  const nonce = generateNonce();
  window.__CSP_NONCE__ = nonce;
  document.documentElement.setAttribute('data-csp-nonce', nonce);

  installObserver(nonce);
  stampAll(nonce);

  // Delayed injection so the initial Vite module graph finishes loading
  // before the strict policy takes effect.
  window.setTimeout(() => injectMeta(nonce), CSP_INJECTION_DELAY_MS);
}
